import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { AlertDialog, Button, Callout, Card, Table, Tag } from '@humanforce/mango-ui-react';
import { userStore } from '../../auth/store';
import { downloadBlob } from './downloadBlob';
import {
  downloadPayslipPdf,
  listPayRunPayslips,
  PayslipRequestError,
  publishPayRunPayslips,
  publishPayslip,
  unpublishPayRunPayslips,
  unpublishPayslip,
} from './payslipsHttpClient';
import type { PayslipDocumentDto } from './payslipsApiTypes';

const PAGE_SIZE = 100;
const GENERIC_ERROR = 'Something went wrong. Try again.';

type BulkAction = 'publish' | 'unpublish';
interface ActionError { title: string; text: string }

const safeMessage = (error: unknown): string => error instanceof PayslipRequestError ? error.message : GENERIC_ERROR;
const isAbort = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const bulkNotice = (action: BulkAction, total: number, changed: number): string => {
  const verb = action === 'publish' ? 'Published' : 'Unpublished';
  const state = action === 'publish' ? 'published' : 'unpublished';
  return `${verb} ${plural(total, 'payslip')} for this pay run (${changed} changed, ${total - changed} already ${state}).`;
};

const BULK_DIALOG_TEST_ID = 'payslips-bulk-dialog';

// Mango's AlertDialog renders its heading without associating it to the alertdialog, leaving the dialog unnamed.
// Same bridge the Pay Run detail screen uses until Mango fixes it.
const useAlertDialogHeadingBridge = (visible: boolean, testId: string) => {
  useEffect(() => {
    if (!visible) return undefined;
    const bindHeading = () => {
      const dialog = document.querySelector<HTMLElement>(`[data-testid="${testId}"][role="alertdialog"]`);
      const heading = dialog?.querySelector<HTMLElement>('h2');
      if (!dialog || !heading) return false;
      heading.id = `${testId}-heading`;
      dialog.setAttribute('aria-labelledby', heading.id);
      return true;
    };
    if (bindHeading()) return undefined;
    const observer = new MutationObserver(() => {
      if (bindHeading()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [testId, visible]);
};

interface ContentProps { businessRecordId: string; payId: string }

const PayslipsPanelContent = ({ businessRecordId, payId }: ContentProps) => {
  const [items, setItems] = useState<PayslipDocumentDto[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<BulkAction | null>(null);
  // State alone cannot stop a second click that lands before React re-renders; the ref closes that window.
  const busyRef = useRef(false);

  const loadFirstPage = useCallback(async (signal?: AbortSignal) => {
    try {
      const result = await listPayRunPayslips(businessRecordId, payId, { skip: 0, take: PAGE_SIZE }, signal);
      if (signal?.aborted) return;
      setItems(result.items);
      setTotal(result.total);
      setLoadError(null);
      setStatus('ready');
    } catch (error) {
      if (signal?.aborted || isAbort(error)) return;
      setLoadError(safeMessage(error));
      setStatus('error');
    }
  }, [businessRecordId, payId]);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    void loadFirstPage(controller.signal);
    return () => controller.abort();
  }, [loadFirstPage]);

  /** Runs one operation at a time; any further trigger while one is in flight is ignored. */
  const runExclusive = async (key: string, operation: () => Promise<void>): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setPending(key);
    setNotice(null);
    setActionError(null);
    try {
      await operation();
    } finally {
      busyRef.current = false;
      setPending(null);
    }
    return true;
  };

  const download = (payslip: PayslipDocumentDto) => runExclusive(`download:${payslip.id}`, async () => {
    try {
      const { blob, fileName } = await downloadPayslipPdf(businessRecordId, payId, payslip.hrPersonId);
      downloadBlob(blob, fileName);
      setNotice(`Your browser downloaded ${fileName}.`);
    } catch (error) {
      setActionError({ title: 'The payslip could not be downloaded', text: safeMessage(error) });
    }
  });

  const togglePublished = (payslip: PayslipDocumentDto) => runExclusive(`toggle:${payslip.id}`, async () => {
    try {
      const updated = await (payslip.isPublished ? unpublishPayslip : publishPayslip)(businessRecordId, payId, payslip.hrPersonId);
      setItems((current) => current.map((item) => item.id === payslip.id ? updated : item));
      setNotice(updated.isPublished ? 'Payslip published.' : 'Payslip unpublished.');
    } catch (error) {
      setActionError({ title: 'The payslip could not be changed', text: safeMessage(error) });
    }
  });

  const confirmBulk = async (action: BulkAction) => {
    const ran = await runExclusive(`bulk:${action}`, async () => {
      try {
        const result = await (action === 'publish' ? publishPayRunPayslips : unpublishPayRunPayslips)(businessRecordId, payId);
        setNotice(bulkNotice(action, result.total, result.changed));
        await loadFirstPage();
      } catch (error) {
        setActionError({ title: 'The pay run payslips could not be changed', text: safeMessage(error) });
      }
    });
    if (ran) setConfirming(null);
  };

  const loadMore = () => runExclusive('load-more', async () => {
    try {
      const result = await listPayRunPayslips(businessRecordId, payId, { skip: items.length, take: PAGE_SIZE });
      setItems((current) => [...current, ...result.items.filter((next) => !current.some((item) => item.id === next.id))]);
      setTotal(result.total);
    } catch (error) {
      setActionError({ title: 'More payslips could not be loaded', text: safeMessage(error) });
    }
  });

  useAlertDialogHeadingBridge(confirming !== null, BULK_DIALOG_TEST_ID);
  const busy = pending !== null;
  const bulkDisabled = busy || status === 'loading' || items.length === 0;

  const columns = [
    { field: 'hrPersonId', header: 'Employee ID' },
    { field: 'paymentDate', header: 'Payment date' },
    { field: 'financialYear', header: 'Financial year' },
    { field: 'contentLength', header: 'Size', body: (row: PayslipDocumentDto) => formatBytes(row.contentLength) },
    {
      field: 'isPublished',
      header: 'Status',
      body: (row: PayslipDocumentDto) => row.isPublished
        ? <Tag label="Published" tone="positive" />
        : <Tag label="Unpublished" tone="neutral" />,
    },
    {
      field: 'actions',
      header: 'Actions',
      body: (row: PayslipDocumentDto) => (
        <div className="mgo-cluster mgo-gap-s">
          <Button label={`Download ${row.hrPersonId}`} icon="download" size="small" variant="secondary" disabled={busy} loading={pending === `download:${row.id}`} onClick={() => void download(row)} />
          <Button
            label={`${row.isPublished ? 'Unpublish' : 'Publish'} ${row.hrPersonId}`}
            size="small"
            variant="tertiary"
            disabled={busy}
            loading={pending === `toggle:${row.id}`}
            onClick={() => void togglePublished(row)}
          />
        </div>
      ),
    },
  ];

  const dialogCopy = confirming === 'publish'
    ? { header: 'Publish all payslips?', label: 'Publish all', body: `This will publish ${plural(total, 'payslip')} for this pay run, marking them as released for employee viewing once employee access is available. You can unpublish them again at any time.` }
    : { header: 'Unpublish all payslips?', label: 'Unpublish all', body: `This will unpublish ${plural(total, 'payslip')} for this pay run, withdrawing them from release for employee viewing. Payroll officers can still download every payslip.` };

  return (
    <section aria-label="Payslips" data-testid="payslips-panel">
      <Card header={<span>Payslips</span>}>
        <div className="mgo-stack mgo-gap-m">
          <p className="mgo-text-s">Payslips are stored privately for this pay run. Publishing marks a payslip as released for employee viewing (employee access is not available yet); payroll officers can always download it.</p>
          <div className="mgo-cluster mgo-gap-s">
            <Button label="Publish all" variant="secondary" size="small" disabled={bulkDisabled} onClick={() => { if (!busy) setConfirming('publish'); }} />
            <Button label="Unpublish all" variant="secondary" size="small" disabled={bulkDisabled} onClick={() => { if (!busy) setConfirming('unpublish'); }} />
          </div>
          {notice && <Callout tone="positive">{notice}</Callout>}
          {actionError && <Callout tone="critical" title={actionError.title}>{actionError.text}</Callout>}
          {loadError && (
            <>
              <Callout tone="critical" title="Payslips unavailable">{loadError}</Callout>
              <div><Button label="Retry loading" size="small" variant="secondary" disabled={busy} onClick={() => { setStatus('loading'); void loadFirstPage(); }} /></div>
            </>
          )}
          {status === 'loading' && items.length === 0 && <p className="mgo-text-s">Loading payslips…</p>}
          {status === 'ready' && items.length === 0 && <p className="mgo-text-s">No payslips have been stored for this pay run yet.</p>}
          {items.length > 0 && <Table value={items} columns={columns} emptyMessage="No payslips have been stored for this pay run yet." />}
          {items.length > 0 && total > items.length && (
            <div className="mgo-cluster mgo-gap-s">
              <p className="mgo-text-s">Showing {items.length} of {total} payslips.</p>
              <Button label="Load more payslips" size="small" variant="secondary" disabled={busy} onClick={() => void loadMore()} />
            </div>
          )}
        </div>
      </Card>
      <AlertDialog
        visible={confirming !== null}
        testId={BULK_DIALOG_TEST_ID}
        header={dialogCopy.header}
        severity="warning"
        confirmLabel={dialogCopy.label}
        cancelLabel="Cancel"
        confirmLoading={pending !== null && pending.startsWith('bulk:')}
        onConfirm={() => { if (confirming) void confirmBulk(confirming); }}
        onCancel={() => { if (!busy) setConfirming(null); }}
      >
        {dialogCopy.body}
      </AlertDialog>
    </section>
  );
};

interface Props { businessRecordId: string; payId: string }

/**
 * PRR-356. Officer-side payslip list on the finalised Pay Run screen. Role gating here is presentation only: the panel is
 * simply not rendered, and nothing is requested, for anyone below Payroll Officer. The server enforces every action.
 */
export const PayslipsPanel = observer(({ businessRecordId, payId }: Props) =>
  userStore.isPayrollOperator ? <PayslipsPanelContent businessRecordId={businessRecordId} payId={payId} /> : null);
